export interface TopicItem {
  id: string;
  title: string;
  primaryKeyword: string;
  secondaryKeywords: string[];
}

export interface TopicGroup {
  id: string;
  name: string;
  color: string;
  topics: TopicItem[];
}

export const TOPICAL_CONTENT_GROUPS: TopicGroup[] = [
  // ───────────────────────────────────────────────
  // GROUP 1: Lifestyle, Culture & Human Stories
  // ───────────────────────────────────────────────
  {
    id: 'lifestyle-culture',
    name: 'Lifestyle, Culture & Human Stories',
    color: '#F59E0B',
    topics: [
      {
        id: 'lc-1',
        title: 'The Citrus Traditions of the Rio Grande Valley: Why South Texas Is America\'s Backyard Orchard',
        primaryKeyword: 'Rio Grande Valley citrus traditions',
        secondaryKeywords: ['South Texas citrus growing', 'backyard citrus orchard Texas', 'Texas citrus history', 'Rio Grande Valley orange groves'],
      },
      {
        id: 'lc-2',
        title: 'Citrus Rituals Around the World: From Japanese Yuzu Baths to Mediterranean Citron Festivals',
        primaryKeyword: 'citrus rituals around the world',
        secondaryKeywords: ['yuzu bath tradition Japan', 'citron festival Mediterranean', 'global citrus ceremonies', 'cultural citrus traditions'],
      },
      {
        id: 'lc-3',
        title: 'Citrus & Family Heritage: Trees People Pass Down Through Generations',
        primaryKeyword: 'citrus trees passed down generations',
        secondaryKeywords: ['family citrus tree heritage', 'heirloom citrus trees', 'generational fruit trees', 'citrus family tradition'],
      },
      {
        id: 'lc-4',
        title: 'How Citrus Shaped Global Trade Routes (and Why It Still Matters Today)',
        primaryKeyword: 'citrus global trade routes history',
        secondaryKeywords: ['citrus trade history', 'orange trade routes', 'citrus economic impact', 'fruit trade global history'],
      },
      {
        id: 'lc-5',
        title: 'The Hidden Stories Behind the World\'s Most Beloved Citrus Trees',
        primaryKeyword: 'stories behind famous citrus trees',
        secondaryKeywords: ['beloved citrus tree history', 'citrus tree origin stories', 'famous orange trees', 'citrus tree legends'],
      },
      {
        id: 'lc-6',
        title: 'Why Home Citrus Growing Is Booming: The Psychology of Growing Your Own Fruit',
        primaryKeyword: 'psychology of growing your own fruit',
        secondaryKeywords: ['home citrus growing trend', 'why people grow citrus at home', 'backyard fruit growing boom', 'mental health benefits growing fruit'],
      },
      {
        id: 'lc-7',
        title: 'The Citrus of Childhood Memories: Why Mandarins Feel Like Magic',
        primaryKeyword: 'mandarin childhood memories',
        secondaryKeywords: ['nostalgia citrus fruit', 'mandarin orange childhood', 'emotional connection citrus', 'Christmas mandarin tradition'],
      },
      {
        id: 'lc-8',
        title: 'What Your Favorite Citrus Variety Says About Your Personality',
        primaryKeyword: 'citrus variety personality quiz',
        secondaryKeywords: ['favorite citrus personality', 'citrus lover personality types', 'what citrus says about you', 'orange vs lemon personality'],
      },
      {
        id: 'lc-9',
        title: 'Why Chefs Fight Over Certain Citrus: The Culinary Prestige Varieties Explained',
        primaryKeyword: 'culinary prestige citrus varieties',
        secondaryKeywords: ['chef favorite citrus', 'gourmet citrus varieties', 'rare citrus for cooking', 'Michelin star citrus ingredients'],
      },
      {
        id: 'lc-10',
        title: 'Citrus Symbolism in Art, Literature, and Religion Across Cultures',
        primaryKeyword: 'citrus symbolism art literature religion',
        secondaryKeywords: ['orange symbolism art', 'lemon symbolism culture', 'citrus in Renaissance art', 'fruit symbolism religion'],
      },
    ],
  },

  // ───────────────────────────────────────────────
  // GROUP 2: Science, History & Genetics
  // ───────────────────────────────────────────────
  {
    id: 'science-history-genetics',
    name: 'Science, History & Genetics',
    color: '#3B82F6',
    topics: [
      {
        id: 'shg-1',
        title: 'The Family Tree of Citrus: How Every Lemon, Lime, and Orange Is Related',
        primaryKeyword: 'citrus family tree genetics',
        secondaryKeywords: ['how citrus varieties are related', 'citrus phylogenetic tree', 'lemon lime orange genetics', 'citrus evolutionary tree'],
      },
      {
        id: 'shg-2',
        title: 'Ancient Citrus: The Original Fruits Before Hybridization Changed Everything',
        primaryKeyword: 'ancient citrus species original',
        secondaryKeywords: ['original citrus fruit history', 'citrus before hybridization', 'ancient citron pomelo mandarin', 'first citrus fruits'],
      },
      {
        id: 'shg-3',
        title: 'The Accidental Mutations That Created the Citrus We Love Today',
        primaryKeyword: 'citrus bud sport mutations',
        secondaryKeywords: ['accidental citrus varieties', 'navel orange bud sport', 'citrus genetic mutations', 'how new citrus varieties form'],
      },
      {
        id: 'shg-4',
        title: 'How Climate Shapes Citrus Flavor: Heat, Cold, Sun, and Soil Explained',
        primaryKeyword: 'how climate affects citrus flavor',
        secondaryKeywords: ['citrus terroir flavor', 'heat units citrus sweetness', 'soil effect on citrus taste', 'sun exposure citrus flavor'],
      },
      {
        id: 'shg-5',
        title: 'Citrus DNA for Beginners: How Hybrids Happen and Why They Taste Different',
        primaryKeyword: 'citrus hybrid genetics beginners',
        secondaryKeywords: ['how citrus hybrids are made', 'citrus cross-pollination explained', 'why citrus hybrids taste different', 'citrus DNA basics'],
      },
      {
        id: 'shg-6',
        title: 'Why Seedlessness Happens: The Biology Behind Navels, Mandarins & Grapefruits',
        primaryKeyword: 'why seedless citrus biology',
        secondaryKeywords: ['seedless orange biology', 'parthenocarpy citrus', 'navel orange seedless genetics', 'seedless mandarin science'],
      },
      {
        id: 'shg-7',
        title: 'The Forgotten Citrus Species You\'ve Never Heard Of',
        primaryKeyword: 'rare forgotten citrus species',
        secondaryKeywords: ['unknown citrus varieties', 'rare citrus species world', 'wild citrus species', 'citrus species you never heard of'],
      },
      {
        id: 'shg-8',
        title: 'Why Some Citrus Smell Floral, Others Smell Sharp, and Some Smell Spicy',
        primaryKeyword: 'why citrus smell different aromatic',
        secondaryKeywords: ['citrus essential oil chemistry', 'lemon vs lime aroma science', 'citrus terpene profiles', 'citrus fragrance differences'],
      },
      {
        id: 'shg-9',
        title: 'How Citrus Oils Work: Why a Leaf Can Change the Flavor of an Entire Dish',
        primaryKeyword: 'citrus oils flavor cooking science',
        secondaryKeywords: ['citrus essential oils cooking', 'makrut lime leaf flavor chemistry', 'citrus oil culinary uses', 'how citrus oils enhance food'],
      },
      {
        id: 'shg-10',
        title: 'Citrus Evolution: How Humans Turned Wild Fruit Into Sweet, Seedless Varieties',
        primaryKeyword: 'citrus domestication evolution history',
        secondaryKeywords: ['citrus domestication timeline', 'wild citrus to modern fruit', 'human selection citrus varieties', 'citrus breeding history'],
      },
    ],
  },

  // ───────────────────────────────────────────────
  // GROUP 3: Grower Psychology, Buyer Education & Emotional Hooks
  // ───────────────────────────────────────────────
  {
    id: 'grower-psychology',
    name: 'Grower Psychology, Buyer Education & Emotional Hooks',
    color: '#10B981',
    topics: [
      {
        id: 'gp-1',
        title: 'Why Growing Citrus Makes You Happier: The Science of Fruit and Mental Health',
        primaryKeyword: 'growing citrus mental health benefits',
        secondaryKeywords: ['gardening happiness science', 'fruit trees mental wellbeing', 'citrus growing therapy', 'horticultural therapy citrus'],
      },
      {
        id: 'gp-2',
        title: 'The #1 Mistake New Citrus Growers Make (and How to Avoid It)',
        primaryKeyword: 'biggest mistake new citrus growers',
        secondaryKeywords: ['citrus growing mistakes beginners', 'new citrus tree care errors', 'common citrus growing problems', 'citrus tree beginner tips'],
      },
      {
        id: 'gp-3',
        title: 'Why Citrus Trees Are the Best Return on Investment in Your Garden',
        primaryKeyword: 'citrus trees garden ROI investment',
        secondaryKeywords: ['fruit tree investment value', 'citrus tree property value', 'cost savings growing citrus', 'garden return on investment'],
      },
      {
        id: 'gp-4',
        title: 'What Professional Citrus Growers Look for in a High-Quality Nursery Tree',
        primaryKeyword: 'how to choose quality nursery citrus tree',
        secondaryKeywords: ['high quality citrus tree signs', 'professional citrus grower tips', 'nursery tree quality indicators', 'what to look for buying citrus tree'],
      },
      {
        id: 'gp-5',
        title: 'How to Choose the Right Citrus Tree for Your Personality & Lifestyle',
        primaryKeyword: 'choose citrus tree for your lifestyle',
        secondaryKeywords: ['best citrus tree for beginners', 'citrus tree personality match', 'right citrus variety for me', 'citrus tree lifestyle guide'],
      },
      {
        id: 'gp-6',
        title: 'Why Citrus Trees Are the Perfect Gift (and What Each Variety Symbolizes)',
        primaryKeyword: 'citrus tree gift meaning symbolism',
        secondaryKeywords: ['citrus tree as gift idea', 'lemon tree gift symbolism', 'orange tree gift meaning', 'fruit tree gift guide'],
      },
      {
        id: 'gp-7',
        title: 'The 10 Most Common Citrus Myths — Debunked',
        primaryKeyword: 'common citrus growing myths debunked',
        secondaryKeywords: ['citrus tree myths facts', 'citrus growing misconceptions', 'fruit tree myths busted', 'citrus care myth vs reality'],
      },
      {
        id: 'gp-8',
        title: 'How to Build a Backyard Citrus Collection That Feels Like a Boutique Orchard',
        primaryKeyword: 'backyard citrus collection orchard',
        secondaryKeywords: ['home citrus orchard design', 'boutique citrus collection', 'backyard fruit tree collection', 'mini citrus orchard planning'],
      },
      {
        id: 'gp-9',
        title: 'Why Citrus Trees Make the Best Patio Statement Plant',
        primaryKeyword: 'citrus tree patio statement plant',
        secondaryKeywords: ['container citrus patio design', 'patio fruit tree ideas', 'best citrus for patio growing', 'citrus tree outdoor decor'],
      },
      {
        id: 'gp-10',
        title: 'How a Single Citrus Tree Can Transform Your Cooking for Life',
        primaryKeyword: 'citrus tree transform home cooking',
        secondaryKeywords: ['fresh citrus cooking benefits', 'homegrown citrus recipes', 'cooking with backyard citrus', 'fresh lemon cooking advantage'],
      },
    ],
  },

  // ───────────────────────────────────────────────
  // GROUP 4: Flavor, Culinary Use & Chef-Driven Stories
  // ───────────────────────────────────────────────
  {
    id: 'flavor-culinary',
    name: 'Flavor, Culinary Use & Chef-Driven Stories',
    color: '#EF4444',
    topics: [
      {
        id: 'fc-1',
        title: 'Why Meyer Lemons Became the Darling of Chefs: The Real Story',
        primaryKeyword: 'why chefs love Meyer lemons',
        secondaryKeywords: ['Meyer lemon chef favorite', 'Meyer lemon culinary history', 'Meyer lemon vs regular lemon cooking', 'Meyer lemon restaurant trend'],
      },
      {
        id: 'fc-2',
        title: 'The Flavor Wheel of Citrus: How Each Variety Creates a Unique Culinary Experience',
        primaryKeyword: 'citrus flavor wheel guide',
        secondaryKeywords: ['citrus flavor profiles compared', 'citrus tasting notes guide', 'different citrus flavors cooking', 'citrus variety flavor comparison'],
      },
      {
        id: 'fc-3',
        title: 'Citrus that Chefs Buy at Any Price: Finger Limes, Yuzu & More',
        primaryKeyword: 'expensive citrus chefs buy finger lime yuzu',
        secondaryKeywords: ['luxury citrus ingredients', 'finger lime culinary use', 'yuzu fine dining', 'rare citrus gourmet cooking'],
      },
      {
        id: 'fc-4',
        title: 'The Best Citrus for Cocktails Ranked by Mixologists',
        primaryKeyword: 'best citrus for cocktails ranked',
        secondaryKeywords: ['citrus cocktail guide mixology', 'lime lemon cocktail comparison', 'yuzu cocktails recipes', 'citrus garnish cocktail guide'],
      },
      {
        id: 'fc-5',
        title: 'Cooking with Citrus Oils: Why Makrut, Yuzu, and Etrog Are Flavor Bombs',
        primaryKeyword: 'cooking with citrus oils makrut yuzu etrog',
        secondaryKeywords: ['makrut lime cooking recipes', 'yuzu oil culinary use', 'etrog cooking applications', 'citrus oil flavor bomb'],
      },
      {
        id: 'fc-6',
        title: 'The Ultimate Guide to Citrus Zest: Which Varieties Shine in Baking?',
        primaryKeyword: 'best citrus zest for baking guide',
        secondaryKeywords: ['citrus zest baking comparison', 'lemon zest vs orange zest baking', 'how to use citrus zest', 'citrus zest flavor guide'],
      },
      {
        id: 'fc-7',
        title: 'The Sweetest Citrus on Earth: A Ranking You Won\'t Expect',
        primaryKeyword: 'sweetest citrus fruit ranking',
        secondaryKeywords: ['sweetest orange variety', 'sugar content citrus comparison', 'sweet citrus varieties list', 'low acid sweet citrus'],
      },
      {
        id: 'fc-8',
        title: 'Citrus for People Who Hate Sour: The Mildest, Sweetest Varieties',
        primaryKeyword: 'mild sweet citrus for people who hate sour',
        secondaryKeywords: ['low acid citrus varieties', 'sweet citrus not sour', 'best citrus for sensitive palate', 'least sour citrus fruit'],
      },
      {
        id: 'fc-9',
        title: 'The Best Citrus for Salads, Marinades, and Seafood',
        primaryKeyword: 'best citrus for salads marinades seafood',
        secondaryKeywords: ['citrus salad recipes varieties', 'citrus marinade guide', 'citrus seafood pairing', 'cooking citrus variety guide'],
      },
      {
        id: 'fc-10',
        title: 'How Different Citrus Varieties Change the Flavor of Your Kitchen',
        primaryKeyword: 'citrus varieties kitchen flavor transformation',
        secondaryKeywords: ['home citrus cooking transformation', 'fresh citrus flavor difference', 'citrus variety cooking guide', 'grow citrus for cooking'],
      },
    ],
  },

  // ───────────────────────────────────────────────
  // GROUP 5: Creative, Viral & Experimental Content
  // ───────────────────────────────────────────────
  {
    id: 'viral-experimental',
    name: 'Creative, Viral & Experimental Content',
    color: '#8B5CF6',
    topics: [
      {
        id: 've-1',
        title: 'Grower Reacts: Grocery Store Citrus vs. Backyard Citrus — A Harsh Comparison',
        primaryKeyword: 'grocery store vs homegrown citrus comparison',
        secondaryKeywords: ['store bought vs backyard fruit', 'commercial citrus vs homegrown', 'supermarket orange vs tree fresh', 'citrus quality comparison'],
      },
      {
        id: 've-2',
        title: 'I Tasted Every Citrus We Sell… Here\'s the Definitive Ranking',
        primaryKeyword: 'citrus variety taste test ranking',
        secondaryKeywords: ['best tasting citrus ranking', 'citrus flavor comparison review', 'all citrus varieties ranked', 'citrus taste test results'],
      },
      {
        id: 've-3',
        title: 'Guess the Citrus by the Peel Challenge (Blindfolded)',
        primaryKeyword: 'citrus identification peel challenge',
        secondaryKeywords: ['identify citrus by smell', 'citrus peel identification guide', 'citrus blind taste test', 'citrus variety identification'],
      },
      {
        id: 've-4',
        title: 'Can You Really Eat a Meyer Lemon Whole With Salt? We Tried It.',
        primaryKeyword: 'eating Meyer lemon whole salt',
        secondaryKeywords: ['Meyer lemon edible raw', 'can you eat lemon rind', 'Meyer lemon eating experiment', 'preserved Meyer lemon taste'],
      },
      {
        id: 've-5',
        title: 'The Citrus Variety Most Likely to Impress Your Neighbors',
        primaryKeyword: 'most impressive citrus tree for yard',
        secondaryKeywords: ['show-off citrus tree varieties', 'unique citrus trees neighbors', 'rare citrus front yard', 'conversation starter citrus tree'],
      },
      {
        id: 've-6',
        title: 'Which Citrus Tree Should You Grow Based on Your Zodiac Sign?',
        primaryKeyword: 'citrus tree zodiac sign match',
        secondaryKeywords: ['zodiac citrus personality', 'astrology citrus tree guide', 'citrus tree horoscope', 'best plant for zodiac sign'],
      },
      {
        id: 've-7',
        title: 'We Asked 100 Citrus Lovers to Rank Their Favorite Varieties — Results Inside',
        primaryKeyword: 'citrus lovers favorite variety poll results',
        secondaryKeywords: ['most popular citrus variety survey', 'favorite citrus fruit poll', 'citrus fan favorite ranking', 'best citrus variety voted'],
      },
      {
        id: 've-8',
        title: 'What Happens When You Cross a Lime with a Kumquat? The Limequat Story',
        primaryKeyword: 'limequat citrus hybrid story',
        secondaryKeywords: ['lime kumquat cross hybrid', 'limequat fruit history', 'unusual citrus hybrids', 'citrus crossbreed stories'],
      },
      {
        id: 've-9',
        title: 'The Most Underrated Citrus Variety Nobody Talks About',
        primaryKeyword: 'most underrated citrus variety',
        secondaryKeywords: ['overlooked citrus varieties', 'hidden gem citrus fruit', 'underappreciated citrus tree', 'unknown but great citrus'],
      },
      {
        id: 've-10',
        title: 'If citrus trees were cars, which model would each variety be?',
        primaryKeyword: 'citrus trees compared to cars',
        secondaryKeywords: ['citrus variety car analogy', 'fun citrus comparisons', 'citrus personality comparison', 'citrus tree metaphor guide'],
      },
    ],
  },

  // ───────────────────────────────────────────────
  // GROUP 6: Ancient History & Civilization / Exploration / Famous People / Economics / Geography
  // ───────────────────────────────────────────────
  {
    id: 'ancient-history-civilization',
    name: 'Ancient History, Civilization & Global Impact',
    color: '#D97706',
    topics: [
      {
        id: 'ah-1',
        title: 'How Citrus First Traveled the Silk Road: The Unknown Journey from China to Persia',
        primaryKeyword: 'citrus Silk Road journey history',
        secondaryKeywords: ['citrus trade ancient China Persia', 'Silk Road fruit trade', 'citrus origins Silk Road', 'how citrus spread globally'],
      },
      {
        id: 'ah-2',
        title: 'The First Citrus in Human History: What the Ancients Grew and Why It Mattered',
        primaryKeyword: 'first citrus human history ancient',
        secondaryKeywords: ['oldest citrus fruit history', 'ancient citrus cultivation', 'first oranges lemons history', 'citrus ancient civilization'],
      },
      {
        id: 'ah-3',
        title: 'Did Alexander the Great Bring Citrus to the West? Exploring Historical Clues',
        primaryKeyword: 'Alexander the Great citrus history',
        secondaryKeywords: ['citrus ancient Greece introduction', 'historical citrus Western spread', 'Alexander Great fruit trade', 'citrus Mediterranean introduction'],
      },
      {
        id: 'ah-4',
        title: 'How Citrus Changed Ancient Medicine in India, China, and Greece',
        primaryKeyword: 'citrus ancient medicine India China Greece',
        secondaryKeywords: ['citrus traditional medicine history', 'lemon ancient medicinal use', 'citrus Ayurveda Chinese medicine', 'ancient citrus healing'],
      },
      {
        id: 'ah-5',
        title: 'The Role of Citron (Etrog) in Ancient Mediterranean Rituals',
        primaryKeyword: 'citron etrog ancient Mediterranean rituals',
        secondaryKeywords: ['etrog Jewish ritual history', 'citron ancient religious use', 'citron Mediterranean ceremony', 'etrog historical significance'],
      },
      {
        id: 'ah-6',
        title: 'Why the Romans Fell in Love With Citrus — And How They Grew It Indoors',
        primaryKeyword: 'Romans citrus cultivation indoor growing',
        secondaryKeywords: ['Roman citrus gardens', 'ancient Rome citrus history', 'Romans growing citrus indoors', 'citrus Roman Empire'],
      },
      {
        id: 'ah-7',
        title: 'The Lost Citrus Gardens of Ancient Persia: Where Aromatic Citrus Was Worshipped',
        primaryKeyword: 'ancient Persian citrus gardens',
        secondaryKeywords: ['Persian paradise garden citrus', 'Iran citrus history ancient', 'citrus worship Persia', 'Persian aromatic citrus gardens'],
      },
      {
        id: 'ah-8',
        title: 'How the Chinese Imperial Dynasty Protected Rare Citrus Varieties',
        primaryKeyword: 'Chinese imperial dynasty citrus history',
        secondaryKeywords: ['Chinese emperor citrus collection', 'imperial citrus cultivation China', 'rare citrus Chinese dynasty', 'mandarin orange Chinese history'],
      },
      {
        id: 'ah-9',
        title: 'Citrus in Ancient Trade Ships: How Fruit Became Treasure',
        primaryKeyword: 'citrus ancient trade ships treasure',
        secondaryKeywords: ['citrus maritime trade history', 'ancient fruit cargo ships', 'citrus valuable trade commodity', 'citrus seafaring history'],
      },
      {
        id: 'ah-10',
        title: 'Why Medieval Monks Were Obsessed With Citron and Bitter Orange',
        primaryKeyword: 'medieval monks citron bitter orange',
        secondaryKeywords: ['monastery citrus cultivation', 'medieval citrus gardens Europe', 'monks growing citrus history', 'citrus medieval medicine'],
      },
      {
        id: 'ah-11',
        title: 'How Citrus Helped European Sailors Survive the Age of Exploration',
        primaryKeyword: 'citrus scurvy Age of Exploration sailors',
        secondaryKeywords: ['limes scurvy prevention history', 'citrus vitamin C sailors', 'naval citrus history', 'scurvy cure citrus history'],
      },
      {
        id: 'ah-12',
        title: 'The First Oranges to Reach the Americas — and Who Planted Them',
        primaryKeyword: 'first oranges Americas history planted',
        secondaryKeywords: ['oranges New World history', 'Columbus citrus Americas', 'first citrus trees America', 'Spanish missionaries citrus'],
      },
      {
        id: 'ah-13',
        title: 'Spanish Missionaries and the Spread of Citrus Across California',
        primaryKeyword: 'Spanish missionaries citrus California spread',
        secondaryKeywords: ['California mission citrus history', 'Spanish citrus California missions', 'citrus California history origins', 'mission orange groves California'],
      },
      {
        id: 'ah-14',
        title: 'How Columbus Accidentally Sparked the Caribbean Citrus Boom',
        primaryKeyword: 'Columbus Caribbean citrus boom history',
        secondaryKeywords: ['citrus Caribbean history', 'Columbus orange seeds Caribbean', 'Caribbean citrus industry origins', 'citrus spread Caribbean islands'],
      },
      {
        id: 'ah-15',
        title: 'The Secret Role of Citrus in Colonial Medicine',
        primaryKeyword: 'citrus colonial medicine history',
        secondaryKeywords: ['citrus health colonial era', 'lemon colonial medicine use', 'citrus vitamin C colonial times', 'colonial America citrus healing'],
      },
      {
        id: 'ah-16',
        title: 'How the British Navy Used Limes to Build an Empire',
        primaryKeyword: 'British Navy limes Limeys history',
        secondaryKeywords: ['Limey origin British sailors', 'lime juice British Navy scurvy', 'Royal Navy citrus history', 'British Empire lime connection'],
      },
      {
        id: 'ah-17',
        title: 'Why Captain Cook Chose Citrus as His Most Valuable Cargo',
        primaryKeyword: 'Captain Cook citrus cargo voyage',
        secondaryKeywords: ['Captain Cook scurvy prevention', 'citrus exploration voyages', 'Cook voyage citrus supplies', 'citrus age of sail'],
      },
      {
        id: 'ah-18',
        title: 'The First Citrus Groves in Florida: The Forgotten Story',
        primaryKeyword: 'first citrus groves Florida history',
        secondaryKeywords: ['Florida citrus industry origins', 'early Florida orange groves', 'Florida citrus plantation history', 'oldest citrus trees Florida'],
      },
      {
        id: 'ah-19',
        title: 'The Global Competition for Citrus Dominance in the 1700s–1900s',
        primaryKeyword: 'global citrus competition 1700s 1900s history',
        secondaryKeywords: ['citrus industry history competition', 'orange empire rivalry', 'citrus trade wars history', 'citrus global dominance history'],
      },
      {
        id: 'ah-20',
        title: 'How Citrus Transformed the Agricultural Identity of the American South',
        primaryKeyword: 'citrus American South agricultural identity',
        secondaryKeywords: ['Southern citrus agriculture history', 'citrus farming American South', 'Florida Texas citrus industry', 'citrus culture American South'],
      },
      {
        id: 'ah-21',
        title: 'How Eliza Tibbets Helped Launch California\'s Citrus Revolution',
        primaryKeyword: 'Eliza Tibbets California citrus revolution',
        secondaryKeywords: ['Eliza Tibbets navel orange', 'Riverside California citrus history', 'Washington navel Tibbets history', 'California orange industry founder'],
      },
      {
        id: 'ah-22',
        title: 'Dr. Mani Skaria and Dr. Richard Hensz and the Texas Rio Red Grapefruit Legacy',
        primaryKeyword: 'Dr Mani Skaria Dr Richard Hensz Rio Red Grapefruit',
        secondaryKeywords: ['Rio Red Grapefruit history Texas', 'Texas citrus pioneers', 'Rio Red Grapefruit origin story', 'Texas A&M citrus research'],
      },
      {
        id: 'ah-23',
        title: 'The Scientists Behind the Meyer Lemon\'s Rise in America',
        primaryKeyword: 'Meyer lemon history America scientists',
        secondaryKeywords: ['Frank Meyer lemon introduction', 'Meyer lemon USDA history', 'Meyer lemon American story', 'Meyer lemon origin history'],
      },
      {
        id: 'ah-24',
        title: 'How Greek Philosophers Wrote About the Mysterious "Golden Apple" (likely a citrus)',
        primaryKeyword: 'Greek philosophers golden apple citrus',
        secondaryKeywords: ['golden apple Greek mythology citrus', 'ancient Greece citrus references', 'Hesperides golden apples citrus', 'citrus Greek philosophy history'],
      },
      {
        id: 'ah-25',
        title: 'The Citrus That Inspired Monet, Cézanne, and Renaissance Painters',
        primaryKeyword: 'citrus art Monet Cézanne Renaissance painters',
        secondaryKeywords: ['citrus still life painting history', 'oranges in Renaissance art', 'impressionist citrus paintings', 'citrus art history symbolism'],
      },
      {
        id: 'ah-26',
        title: 'How Yuzu Became a Culinary Obsession Among Michelin-Star Chefs',
        primaryKeyword: 'yuzu Michelin star chef obsession history',
        secondaryKeywords: ['yuzu fine dining history', 'yuzu culinary rise', 'why chefs love yuzu', 'yuzu restaurant trend global'],
      },
      {
        id: 'ah-27',
        title: 'The Presidential Citrus Grove: U.S. Presidents Who Planted Citrus',
        primaryKeyword: 'US Presidents citrus grove history',
        secondaryKeywords: ['presidential citrus trees', 'White House citrus history', 'Jefferson citrus planting', 'presidents who grew fruit trees'],
      },
      {
        id: 'ah-28',
        title: 'How Marco Polo Described Asian Citrus to Europeans for the First Time',
        primaryKeyword: 'Marco Polo Asian citrus European description',
        secondaryKeywords: ['Marco Polo citrus accounts', 'medieval European citrus discovery', 'citrus trade route travelers', 'Marco Polo fruit descriptions'],
      },
      {
        id: 'ah-29',
        title: 'Citrus in Persian Royal Gardens — A Symbol of Wealth and Power',
        primaryKeyword: 'Persian royal gardens citrus wealth symbol',
        secondaryKeywords: ['Iranian citrus garden history', 'Persian paradise gardens fruit', 'citrus royalty symbol Persia', 'ancient Iranian orange gardens'],
      },
      {
        id: 'ah-30',
        title: 'Why Thomas Jefferson Planted Citrus and Experimented With Hybrids',
        primaryKeyword: 'Thomas Jefferson citrus experiments Monticello',
        secondaryKeywords: ['Jefferson Monticello citrus', 'Thomas Jefferson fruit trees', 'founding fathers citrus history', 'Jefferson agricultural experiments'],
      },
      {
        id: 'ah-31',
        title: 'How Citrus Became One of the Most Valuable Crops in American History',
        primaryKeyword: 'citrus most valuable crop American history',
        secondaryKeywords: ['citrus industry American economy', 'orange industry US history', 'citrus agriculture economic impact', 'American citrus industry value'],
      },
      {
        id: 'ah-32',
        title: 'The Rise and Fall of Florida\'s Citrus Empire',
        primaryKeyword: 'Florida citrus empire rise and fall',
        secondaryKeywords: ['Florida orange industry decline', 'Florida citrus history timeline', 'what happened Florida citrus', 'Florida orange groves disappearing'],
      },
      {
        id: 'ah-33',
        title: 'How Citrus Built Riverside, CA Into the Richest City in America in the 1890s',
        primaryKeyword: 'Riverside California citrus richest city 1890s',
        secondaryKeywords: ['Riverside CA orange industry history', 'California citrus boom Riverside', 'richest city America citrus', 'Riverside orange empire history'],
      },
      {
        id: 'ah-34',
        title: 'Why Citrus Was Once Considered a Luxury Food Reserved for Royalty',
        primaryKeyword: 'citrus luxury food royalty history',
        secondaryKeywords: ['oranges luxury royal fruit', 'citrus aristocracy history', 'expensive fruit royalty Europe', 'orange luxury Renaissance'],
      },
      {
        id: 'ah-35',
        title: 'Citrus in World War II: Rationing, Vitamin C, and Global Supply Chains',
        primaryKeyword: 'citrus World War II rationing vitamin C',
        secondaryKeywords: ['WWII citrus supply chain', 'vitamin C wartime importance', 'citrus rationing World War 2', 'military citrus history'],
      },
      {
        id: 'ah-36',
        title: 'How Citrus Shaped Immigration and Labor Patterns in California',
        primaryKeyword: 'citrus immigration labor California history',
        secondaryKeywords: ['California citrus workers history', 'citrus picking labor history', 'agricultural immigration California citrus', 'citrus farm labor history'],
      },
      {
        id: 'ah-37',
        title: 'The Economic Battle Between Sweet Oranges and Bitter Oranges in Europe',
        primaryKeyword: 'sweet oranges vs bitter oranges Europe economics',
        secondaryKeywords: ['Seville bitter orange European history', 'sweet orange replaced bitter orange', 'citrus economics European history', 'orange market history Europe'],
      },
      {
        id: 'ah-38',
        title: 'Why Citrus Became a Symbol of Wealth in Victorian England',
        primaryKeyword: 'citrus wealth symbol Victorian England',
        secondaryKeywords: ['Victorian orangery history', 'citrus status symbol England', 'Victorian England orange culture', 'citrus Victorian era luxury'],
      },
      {
        id: 'ah-39',
        title: 'How Citrus Farming Fueled the Development of Irrigation in the American West',
        primaryKeyword: 'citrus farming irrigation American West history',
        secondaryKeywords: ['California irrigation citrus history', 'citrus water infrastructure West', 'American West irrigation citrus', 'citrus agriculture water history'],
      },
      {
        id: 'ah-40',
        title: 'The Global Citrus Trade Today: Which Countries Dominate and Why',
        primaryKeyword: 'global citrus trade countries dominate',
        secondaryKeywords: ['top citrus producing countries', 'world citrus trade statistics', 'citrus export countries ranking', 'global orange production leaders'],
      },
      {
        id: 'ah-41',
        title: 'The Citrus of the Himalayas: How Wild Citrus Still Grows Where It Began',
        primaryKeyword: 'wild citrus Himalayas origin species',
        secondaryKeywords: ['Himalayan wild citrus species', 'citrus origin northeast India', 'wild citrus natural habitat', 'original citrus species Himalayas'],
      },
      {
        id: 'ah-42',
        title: 'Japan\'s Citrus Islands — The Sacred Lands of Yuzu, Mikan & Ponkan',
        primaryKeyword: 'Japan citrus islands yuzu mikan ponkan',
        secondaryKeywords: ['Japanese citrus growing regions', 'yuzu farming Japan islands', 'mikan mandarin Japan', 'Japanese citrus culture tradition'],
      },
      {
        id: 'ah-43',
        title: 'The Citrus Belt of Texas: Why the Rio Grande Valley Became a Citrus Powerhouse',
        primaryKeyword: 'Texas citrus belt Rio Grande Valley powerhouse',
        secondaryKeywords: ['Rio Grande Valley citrus industry', 'Texas citrus farming history', 'South Texas citrus production', 'Texas citrus belt region'],
      },
      {
        id: 'ah-44',
        title: 'Citrus in Moroccan Gardens: The Secret Influence of Andalusian Agriculture',
        primaryKeyword: 'Moroccan gardens citrus Andalusian influence',
        secondaryKeywords: ['Moroccan citrus garden design', 'Andalusian citrus agriculture', 'Morocco orange groves history', 'Islamic citrus garden tradition'],
      },
      {
        id: 'ah-45',
        title: 'Why Sicily Became the World Capital of Blood Oranges',
        primaryKeyword: 'Sicily blood orange capital world history',
        secondaryKeywords: ['Sicilian blood orange history', 'Tarocco blood orange Sicily', 'Italy blood orange production', 'Sicily citrus industry'],
      },
      {
        id: 'ah-46',
        title: 'The Sacred Citrus of India: How Lemons and Limes Are Used in Rituals',
        primaryKeyword: 'sacred citrus India lemon lime rituals',
        secondaryKeywords: ['Indian lemon ritual spiritual', 'lime Hindu ceremony', 'citrus Indian religious tradition', 'lemon evil eye protection India'],
      },
      {
        id: 'ah-47',
        title: 'Australia\'s Native Citrus: Finger Limes and Desert Limes Before Europeans Arrived',
        primaryKeyword: 'Australia native citrus finger lime desert lime',
        secondaryKeywords: ['Australian bush tucker citrus', 'finger lime Aboriginal use', 'native Australian citrus species', 'desert lime Australian history'],
      },
      {
        id: 'ah-48',
        title: 'The Mythology of Oranges in China: A Symbol of Luck, Wealth & Longevity',
        primaryKeyword: 'oranges China mythology luck wealth symbol',
        secondaryKeywords: ['Chinese New Year orange symbolism', 'mandarin luck China tradition', 'citrus Chinese mythology', 'orange prosperity symbol China'],
      },
      {
        id: 'ah-49',
        title: 'Citrus in Modern Food Culture: How Globalization Spread Exotic Varieties',
        primaryKeyword: 'citrus globalization exotic varieties modern food',
        secondaryKeywords: ['exotic citrus varieties global spread', 'citrus modern food trend', 'rare citrus global availability', 'globalization citrus access'],
      },
      {
        id: 'ah-50',
        title: 'The Climate Secrets Behind Why Certain Regions Produce the Best Citrus on Earth',
        primaryKeyword: 'climate secrets best citrus regions world',
        secondaryKeywords: ['best climate growing citrus', 'citrus terroir best regions', 'why certain regions produce best citrus', 'citrus growing zone secrets'],
      },
    ],
  },

  // ───────────────────────────────────────────────
  // GROUP 7: Royalty, Container Growing, Prestige & Hobbyist Culture
  // ───────────────────────────────────────────────
  {
    id: 'royalty-prestige-hobbyist',
    name: 'Royalty, Prestige, Container Growing & Hobbyist Culture',
    color: '#EC4899',
    topics: [
      {
        id: 'rph-1',
        title: 'The Orangeries of European Palaces: How Citrus Became a Symbol of Royal Power',
        primaryKeyword: 'European palace orangeries citrus royal power',
        secondaryKeywords: ['orangery history European palaces', 'royal citrus gardens Europe', 'citrus symbol royalty power', 'palace orange house history'],
      },
      {
        id: 'rph-2',
        title: 'Louis XIV\'s Citrus Collection: Inside the Sun King\'s Legendary Orangerie',
        primaryKeyword: 'Louis XIV orangerie Versailles citrus collection',
        secondaryKeywords: ['Versailles orangerie history', 'Sun King citrus trees', 'Louis XIV orange collection', 'French royal citrus garden'],
      },
      {
        id: 'rph-3',
        title: 'The Medicis and Their Forbidden Citrus Gardens in Renaissance Florence',
        primaryKeyword: 'Medici family citrus gardens Renaissance Florence',
        secondaryKeywords: ['Medici orange garden history', 'Renaissance citrus cultivation', 'Florence Medici fruit gardens', 'Italian Renaissance citrus'],
      },
      {
        id: 'rph-4',
        title: 'Citrus in the Forbidden City: Imperial Chinese Citrus and What They Symbolized',
        primaryKeyword: 'Forbidden City citrus imperial Chinese symbolism',
        secondaryKeywords: ['Chinese imperial citrus symbol', 'Forbidden City fruit gardens', 'mandarin orange Chinese emperor', 'citrus Beijing imperial garden'],
      },
      {
        id: 'rph-5',
        title: 'Queen Victoria\'s Obsession With Citrus Perfumes',
        primaryKeyword: 'Queen Victoria citrus perfume obsession',
        secondaryKeywords: ['Victorian citrus perfume history', 'Queen Victoria fragrance citrus', 'citrus perfume Victorian era', 'royal citrus aromatics'],
      },
      {
        id: 'rph-6',
        title: 'The Citrus Trees of Versailles — How They Survived Wars and Revolutions',
        primaryKeyword: 'Versailles citrus trees survived wars revolutions',
        secondaryKeywords: ['Versailles orangerie trees history', 'oldest citrus trees France', 'citrus French Revolution survival', 'Versailles garden citrus history'],
      },
      {
        id: 'rph-7',
        title: 'The Royal Citrus of Persia: Why Ancient Kings Treasured Citron and Bitter Orange',
        primaryKeyword: 'Persian kings citron bitter orange royal',
        secondaryKeywords: ['ancient Persia citrus royalty', 'citron royal significance Persia', 'bitter orange Persian kings', 'Persian royal fruit gardens'],
      },
      {
        id: 'rph-8',
        title: 'The Tsars\' Winter Citrus Palaces in St. Petersburg',
        primaryKeyword: 'Russian Tsars citrus palaces St Petersburg',
        secondaryKeywords: ['Russian royal orangery history', 'Tsar citrus winter palace', 'St Petersburg citrus greenhouse', 'Russian imperial citrus culture'],
      },
      {
        id: 'rph-9',
        title: 'How Japanese Emperors Celebrated Yuzu During Winter Solstice Traditions',
        primaryKeyword: 'Japanese emperor yuzu winter solstice tradition',
        secondaryKeywords: ['yuzu bath winter solstice Japan', 'toji yuzu tradition Japanese', 'Japanese imperial yuzu ceremony', 'yuzu winter celebration Japan'],
      },
      {
        id: 'rph-10',
        title: 'The Hidden Citrus Gardens of Moroccan Sultans',
        primaryKeyword: 'Moroccan sultans hidden citrus gardens',
        secondaryKeywords: ['Moroccan royal citrus gardens', 'sultan orange gardens Morocco', 'Moroccan palace citrus', 'Marrakech citrus garden history'],
      },
      {
        id: 'rph-11',
        title: 'Why European Nobility Grew Citrus in Giant Ornamental Containers',
        primaryKeyword: 'European nobility citrus ornamental containers history',
        secondaryKeywords: ['aristocratic citrus pot growing', 'ornamental citrus containers Europe', 'noble citrus container tradition', 'luxury citrus pot history'],
      },
      {
        id: 'rph-12',
        title: 'The Ancient Roman Terra-Cotta Citrus Pot Tradition',
        primaryKeyword: 'ancient Roman terra cotta citrus pots',
        secondaryKeywords: ['Roman citrus container growing', 'terra cotta citrus pots history', 'Roman garden citrus tradition', 'ancient container citrus growing'],
      },
      {
        id: 'rph-13',
        title: 'How Maritime Traders Carried Citrus in Containers Across the Seas',
        primaryKeyword: 'maritime traders citrus containers sea voyages',
        secondaryKeywords: ['citrus ship containers trade', 'portable citrus trees sea', 'citrus maritime container history', 'sailing citrus trees history'],
      },
      {
        id: 'rph-14',
        title: 'The Victorian Citrus Conservatory Craze — Indoor Citrus as a Status Symbol',
        primaryKeyword: 'Victorian citrus conservatory indoor status symbol',
        secondaryKeywords: ['Victorian greenhouse citrus', 'conservatory citrus status symbol', 'indoor citrus Victorian era', 'Victorian orangery conservatory'],
      },
      {
        id: 'rph-15',
        title: 'A History of Portable Citrus: From Monasteries to Modern Patios',
        primaryKeyword: 'portable citrus history monasteries modern patios',
        secondaryKeywords: ['container citrus complete history', 'citrus pot growing through ages', 'monastery to patio citrus', 'evolution portable citrus growing'],
      },
      {
        id: 'rph-16',
        title: 'The First Citrus Greenhouses and How They Changed Horticulture',
        primaryKeyword: 'first citrus greenhouses history horticulture',
        secondaryKeywords: ['citrus greenhouse invention history', 'orangery greenhouse evolution', 'early citrus protected cultivation', 'greenhouse citrus growing origins'],
      },
      {
        id: 'rph-17',
        title: 'How Container Citrus Shaped Urban Gardening in 18th Century London',
        primaryKeyword: 'container citrus urban gardening 18th century London',
        secondaryKeywords: ['London citrus growing history', 'urban citrus container 1700s', 'Georgian era citrus London', 'city citrus growing history'],
      },
      {
        id: 'rph-18',
        title: 'The French Art of Citrus Bonsai and Miniature Orangeries',
        primaryKeyword: 'French citrus bonsai miniature orangeries',
        secondaryKeywords: ['citrus bonsai art France', 'miniature orange tree French', 'French citrus growing traditions', 'bonsai citrus tree history'],
      },
      {
        id: 'rph-19',
        title: 'The Revival of Container Citrus in Modern Apartment Living',
        primaryKeyword: 'container citrus modern apartment living revival',
        secondaryKeywords: ['apartment citrus growing trend', 'indoor citrus modern living', 'container citrus urban apartments', 'growing citrus small spaces'],
      },
      {
        id: 'rph-20',
        title: 'How Climate Zones Inspired the Evolution of Container Citrus Culture',
        primaryKeyword: 'climate zones container citrus culture evolution',
        secondaryKeywords: ['citrus container growing climate adaptation', 'cold climate citrus containers', 'citrus pot growing climate zones', 'container citrus climate history'],
      },
      {
        id: 'rph-21',
        title: 'The Most Expensive Citrus Ever Sold at Auction',
        primaryKeyword: 'most expensive citrus sold auction record',
        secondaryKeywords: ['rare citrus auction prices', 'expensive citrus fruit record', 'luxury citrus market value', 'citrus auction history records'],
      },
      {
        id: 'rph-22',
        title: 'Why Dekopon (Shiranui) Once Sold for $10 Each in Japan',
        primaryKeyword: 'Dekopon Shiranui expensive citrus Japan price',
        secondaryKeywords: ['Dekopon luxury mandarin Japan', 'Shiranui premium citrus', 'expensive Japanese mandarin', 'Dekopon price history Japan'],
      },
      {
        id: 'rph-23',
        title: 'Yuzu\'s Rise to Luxury Ingredient Status in Fine Dining',
        primaryKeyword: 'yuzu luxury ingredient fine dining rise',
        secondaryKeywords: ['yuzu Michelin star ingredient', 'yuzu gourmet status history', 'yuzu fine dining trend', 'yuzu premium ingredient'],
      },
      {
        id: 'rph-24',
        title: 'Why Buddha\'s Hand Became a Collector\'s Fruit for Fragrance Houses',
        primaryKeyword: 'Buddhas Hand citrus fragrance collector fruit',
        secondaryKeywords: ['Buddhas Hand perfume ingredient', 'citron fragrance collector', 'Buddhas Hand aromatic value', 'Buddhas Hand luxury fragrance'],
      },
      {
        id: 'rph-25',
        title: 'The Cultural and Economic Value of Etrog in Jewish Communities',
        primaryKeyword: 'etrog cultural economic value Jewish communities',
        secondaryKeywords: ['etrog Sukkot market value', 'etrog Jewish tradition economics', 'kosher etrog market', 'etrog citron Jewish significance'],
      },
      {
        id: 'rph-26',
        title: 'Finger Lime\'s Journey From Aboriginal Bush Tucker to $40/LB Gourmet Product',
        primaryKeyword: 'finger lime Aboriginal bush tucker gourmet journey',
        secondaryKeywords: ['finger lime price gourmet market', 'Australian finger lime caviar', 'finger lime luxury ingredient history', 'finger lime bush food to fine dining'],
      },
      {
        id: 'rph-27',
        title: 'The Rare Citrus Varieties Elite Chefs Fight to Get First Each Season',
        primaryKeyword: 'rare citrus elite chefs seasonal varieties',
        secondaryKeywords: ['chef favorite rare citrus', 'seasonal rare citrus varieties', 'elite restaurant citrus sourcing', 'limited citrus varieties chefs'],
      },
      {
        id: 'rph-28',
        title: 'Why Ancient Citron Was Worth More Than Gold in Some Regions',
        primaryKeyword: 'ancient citron worth more than gold history',
        secondaryKeywords: ['citron precious ancient value', 'citrus more valuable gold', 'citron ancient trade value', 'most valuable ancient fruit'],
      },
      {
        id: 'rph-29',
        title: 'The Lost Citrus Varieties That Were Once Priceless',
        primaryKeyword: 'lost citrus varieties priceless extinct history',
        secondaryKeywords: ['extinct citrus varieties', 'lost rare citrus history', 'forgotten citrus species valuable', 'citrus varieties that disappeared'],
      },
      {
        id: 'rph-30',
        title: 'Which Modern Citrus Varieties Are Becoming Investment Trees?',
        primaryKeyword: 'citrus tree investment modern varieties value',
        secondaryKeywords: ['citrus tree investment potential', 'valuable citrus varieties to grow', 'citrus tree appreciating value', 'best citrus trees for investment'],
      },
      {
        id: 'rph-31',
        title: 'Japan\'s Mikan Mania — Why Mandarins Became a Cultural Obsession',
        primaryKeyword: 'Japan mikan mania mandarin cultural obsession',
        secondaryKeywords: ['Japanese mikan culture', 'mandarin Japanese tradition', 'mikan orange Japan popularity', 'Japanese mandarin obsession history'],
      },
      {
        id: 'rph-32',
        title: 'Australia\'s Finger Lime Frenzy Among Home Growers',
        primaryKeyword: 'Australia finger lime home grower frenzy',
        secondaryKeywords: ['growing finger limes Australia', 'finger lime home garden trend', 'Australian finger lime craze', 'finger lime backyard growing'],
      },
      {
        id: 'rph-33',
        title: 'The U.S. Backyard Citrus Boom and DIY Orchard Culture',
        primaryKeyword: 'US backyard citrus boom DIY orchard',
        secondaryKeywords: ['backyard citrus growing trend America', 'DIY citrus orchard home', 'home citrus growing boom US', 'backyard fruit orchard trend'],
      },
      {
        id: 'rph-34',
        title: 'Europe\'s Rising Trend of Apartment Citrus Trees',
        primaryKeyword: 'Europe apartment citrus tree trend',
        secondaryKeywords: ['European apartment citrus growing', 'indoor citrus tree Europe trend', 'urban citrus Europe apartments', 'European indoor citrus trend'],
      },
      {
        id: 'rph-35',
        title: 'The Online Citrus Collector Communities You\'ve Never Heard Of',
        primaryKeyword: 'online citrus collector communities forums',
        secondaryKeywords: ['citrus enthusiast online groups', 'rare citrus collector community', 'citrus trading online forums', 'citrus grower social media groups'],
      },
      {
        id: 'rph-36',
        title: 'Why Hobbyists in California Hunt Down Rare UCR Varieties',
        primaryKeyword: 'California hobbyists rare UCR citrus varieties',
        secondaryKeywords: ['UCR citrus variety collection', 'rare citrus California collectors', 'University California Riverside citrus', 'UCR citrus variety hunters'],
      },
      {
        id: 'rph-37',
        title: 'The Asian Hybrid Craze: Citrus Collectors Trading Rare Fruits Online',
        primaryKeyword: 'Asian citrus hybrid collectors trading online',
        secondaryKeywords: ['rare Asian citrus hybrids', 'citrus collector trading community', 'exotic citrus hybrid online market', 'rare citrus fruit trading'],
      },
      {
        id: 'rph-38',
        title: 'Florida\'s Mini-Orchards: How Small Yards Are Producing Giant Harvests',
        primaryKeyword: 'Florida mini orchard small yard citrus harvest',
        secondaryKeywords: ['small yard citrus orchard Florida', 'Florida backyard citrus production', 'mini orchard high yield citrus', 'small space citrus growing Florida'],
      },
      {
        id: 'rph-39',
        title: 'Why Chefs Are Becoming Citrus Hobbyists Themselves',
        primaryKeyword: 'chefs growing citrus hobbyist trend',
        secondaryKeywords: ['restaurant chef citrus garden', 'chefs who grow citrus', 'chef citrus hobby trend', 'professional chefs growing fruit'],
      },
      {
        id: 'rph-40',
        title: 'The Social Media Rise of "Citrus Influencers"',
        primaryKeyword: 'citrus influencers social media rise',
        secondaryKeywords: ['citrus growing social media', 'citrus content creators', 'plant influencer citrus', 'citrus TikTok Instagram trend'],
      },
      {
        id: 'rph-41',
        title: 'How Citrus Became the First Real "Luxury Produce" in Europe',
        primaryKeyword: 'citrus first luxury produce Europe history',
        secondaryKeywords: ['luxury fruit European history', 'citrus premium produce origins', 'first expensive fruit Europe', 'citrus luxury market history'],
      },
      {
        id: 'rph-42',
        title: 'The Global Citrus Triangle: California, Spain, and Japan',
        primaryKeyword: 'global citrus triangle California Spain Japan',
        secondaryKeywords: ['world top citrus regions', 'California Spain Japan citrus', 'citrus producing powerhouses', 'global citrus production triangle'],
      },
      {
        id: 'rph-43',
        title: 'How Citrus Architecture (orangeries) Boosted European Economies',
        primaryKeyword: 'citrus orangery architecture European economy',
        secondaryKeywords: ['orangery economic impact Europe', 'citrus architecture European history', 'orangery boost local economy', 'citrus greenhouse economic history'],
      },
      {
        id: 'rph-44',
        title: 'Citrus as Currency: When Fruit Was Used as Payment',
        primaryKeyword: 'citrus used as currency payment history',
        secondaryKeywords: ['fruit as currency ancient', 'citrus trade barter history', 'oranges payment historical', 'citrus monetary value ancient'],
      },
      {
        id: 'rph-45',
        title: 'How Citrus Taxes Shaped Ancient Empires',
        primaryKeyword: 'citrus taxes ancient empires history',
        secondaryKeywords: ['fruit tax ancient empire', 'citrus trade taxation history', 'citrus levy ancient world', 'agricultural tax citrus empire'],
      },
      {
        id: 'rph-46',
        title: 'The Merchant Families Who Built Their Wealth on Citrus Trade',
        primaryKeyword: 'merchant families citrus trade wealth history',
        secondaryKeywords: ['citrus trade wealthy families', 'orange merchants history', 'citrus trade dynasty families', 'merchant citrus empire history'],
      },
      {
        id: 'rph-47',
        title: 'Citrus Smuggling: The True Story of Forbidden Fruit Varieties',
        primaryKeyword: 'citrus smuggling forbidden fruit varieties history',
        secondaryKeywords: ['citrus variety smuggling history', 'forbidden citrus trade', 'illegal citrus import stories', 'citrus plant smuggling'],
      },
      {
        id: 'rph-48',
        title: 'Why Spain Guarded Its Blood Orange Varieties for Centuries',
        primaryKeyword: 'Spain guarded blood orange varieties centuries',
        secondaryKeywords: ['Spanish blood orange protection', 'Spain citrus variety secrets', 'blood orange Spanish monopoly', 'Spain citrus export control history'],
      },
      {
        id: 'rph-49',
        title: 'The Economics of Rare Citrus: What Drives a Fruit to Cult Status',
        primaryKeyword: 'rare citrus economics cult status fruit',
        secondaryKeywords: ['rare fruit cult following economics', 'citrus market premium pricing', 'what makes citrus rare valuable', 'cult citrus variety economics'],
      },
      {
        id: 'rph-50',
        title: 'How Citrus Tourism Is Becoming a New Global Industry',
        primaryKeyword: 'citrus tourism global industry trend',
        secondaryKeywords: ['citrus farm tourism', 'orange grove tourism', 'agritourism citrus industry', 'citrus tourism destinations worldwide'],
      },
    ],
  },

  // ───────────────────────────────────────────────
  // GROUP 8: Citrus & Christianity
  // ───────────────────────────────────────────────
  {
    id: 'citrus-christianity',
    name: 'Citrus & Christianity',
    color: '#6366F1',
    topics: [
      {
        id: 'cc-1',
        title: 'Why Early Christians Associated Lemons with Purity and Spiritual Cleansing',
        primaryKeyword: 'early Christians lemon purity spiritual cleansing',
        secondaryKeywords: ['lemon Christian symbolism purity', 'citrus spiritual cleansing Christianity', 'lemon religious purification', 'Christian fruit symbolism lemon'],
      },
      {
        id: 'cc-2',
        title: 'Citrus as a Symbol of Resurrection: Why Fruit Trees Mattered in Christian Eschatology',
        primaryKeyword: 'citrus resurrection symbol Christian eschatology',
        secondaryKeywords: ['fruit tree resurrection symbolism', 'citrus Christian afterlife symbol', 'Christian eschatology fruit trees', 'citrus eternal life symbolism'],
      },
      {
        id: 'cc-3',
        title: 'How Medieval Christians Used Citrus Imagery to Explain the Fruits of the Spirit',
        primaryKeyword: 'medieval Christians citrus imagery Fruits of Spirit',
        secondaryKeywords: ['Fruits of Spirit citrus analogy', 'medieval Christian citrus symbolism', 'biblical fruit imagery citrus', 'Galatians fruits citrus analogy'],
      },
      {
        id: 'cc-4',
        title: 'The Role of Citrus Fragrance in Monastic Contemplative Practices',
        primaryKeyword: 'citrus fragrance monastic contemplative practices',
        secondaryKeywords: ['monastery citrus aromatherapy', 'monastic garden citrus fragrance', 'contemplative prayer citrus scent', 'monk citrus meditation practice'],
      },
      {
        id: 'cc-5',
        title: 'How Christian Mystics Compared the Bitterness of Trials to the Bitterness of Early Citrus',
        primaryKeyword: 'Christian mystics bitterness trials citrus metaphor',
        secondaryKeywords: ['Christian suffering citrus analogy', 'bitter citrus spiritual lesson', 'mystic citrus metaphor trials', 'bitter fruit Christian teaching'],
      },
      {
        id: 'cc-6',
        title: 'Why Christian Theologians Connected Citrus Blossoms to the Virgin Mary',
        primaryKeyword: 'citrus blossoms Virgin Mary Christian theology',
        secondaryKeywords: ['orange blossom Virgin Mary', 'Marian citrus symbolism', 'Virgin Mary flower citrus', 'Christian theology citrus flowers'],
      },
      {
        id: 'cc-7',
        title: 'How Citrus Trees Became Symbols of Stability and Perseverance in Early Christian Homilies',
        primaryKeyword: 'citrus trees stability perseverance Christian homilies',
        secondaryKeywords: ['citrus evergreen Christian symbolism', 'tree stability Christian sermon', 'citrus perseverance metaphor church', 'early Christian tree symbolism'],
      },
      {
        id: 'cc-8',
        title: 'The Depiction of Citrus in Christian "Paradise Gardens" During the Middle Ages',
        primaryKeyword: 'citrus Paradise Gardens Christian Middle Ages',
        secondaryKeywords: ['medieval paradise garden citrus', 'hortus conclusus citrus', 'Christian Eden garden citrus', 'medieval monastery garden citrus'],
      },
      {
        id: 'cc-9',
        title: 'How Early Christians Used Citrus Analogies to Explain Grafting Theology (Romans 11)',
        primaryKeyword: 'citrus grafting theology Romans 11 Christian',
        secondaryKeywords: ['Romans 11 olive grafting citrus', 'grafting theology biblical citrus', 'Christian grafting analogy fruit tree', 'biblical graft metaphor citrus'],
      },
      {
        id: 'cc-10',
        title: 'Why Christian Pilgrimage Routes Across Europe Often Featured Citrus Groves',
        primaryKeyword: 'Christian pilgrimage routes citrus groves Europe',
        secondaryKeywords: ['pilgrim citrus gardens Europe', 'pilgrimage route fruit groves', 'Christian pilgrim citrus stop', 'European pilgrimage citrus tradition'],
      },
      {
        id: 'cc-11',
        title: 'How Citrus Became a Staple in Monastic Infirmaries Due to Its Healing Properties',
        primaryKeyword: 'citrus monastic infirmary healing properties',
        secondaryKeywords: ['monastery citrus medicine', 'monastic infirmary citrus healing', 'monk citrus healing tradition', 'citrus medieval monastery medicine'],
      },
      {
        id: 'cc-12',
        title: 'The First Citrus Groves Planted by Catholic Missions in the Americas',
        primaryKeyword: 'Catholic missions citrus groves Americas first',
        secondaryKeywords: ['mission citrus gardens Americas', 'Catholic missionary citrus planting', 'Spanish mission orange groves', 'mission era citrus cultivation'],
      },
      {
        id: 'cc-13',
        title: 'Citrus as a Conversion Tool: How Missionaries Used Fruit to Demonstrate Abundance',
        primaryKeyword: 'citrus missionary conversion abundance demonstration',
        secondaryKeywords: ['missionary fruit garden conversion', 'citrus abundance demonstration missions', 'Christian missionary gardening tool', 'fruit garden evangelical strategy'],
      },
      {
        id: 'cc-14',
        title: 'Why Jesuit Botanists Documented Citrus More Than Any Other Fruit',
        primaryKeyword: 'Jesuit botanists citrus documentation history',
        secondaryKeywords: ['Jesuit citrus botany research', 'Jesuit scientific citrus records', 'Jesuits fruit documentation history', 'Jesuit citrus botanical studies'],
      },
      {
        id: 'cc-15',
        title: 'How Citrus Helped Reshape the Economy of Christianized Regions',
        primaryKeyword: 'citrus economy Christianized regions history',
        secondaryKeywords: ['Christian region citrus agriculture', 'citrus economic transformation Christianity', 'faith-based citrus farming history', 'Christian citrus economy impact'],
      },
      {
        id: 'cc-16',
        title: 'Citrus as a Symbol of "New Life" in Early American Christian Homesteads',
        primaryKeyword: 'citrus new life symbol American Christian homesteads',
        secondaryKeywords: ['Christian homestead fruit tree', 'new life citrus symbolism America', 'early American Christian citrus', 'homestead fruit tree spiritual meaning'],
      },
      {
        id: 'cc-17',
        title: 'Catholic vs. Protestant Citrus Cultivation Approaches in Colonial America',
        primaryKeyword: 'Catholic Protestant citrus cultivation colonial America',
        secondaryKeywords: ['colonial citrus growing religious differences', 'Catholic mission vs Protestant farm citrus', 'denomination citrus agriculture history', 'religious citrus farming colonial'],
      },
      {
        id: 'cc-18',
        title: 'The Citrus Varieties Preferred by Different Religious Orders',
        primaryKeyword: 'citrus varieties religious orders preferred',
        secondaryKeywords: ['Franciscan Jesuit citrus varieties', 'monastery citrus variety preferences', 'religious order fruit garden choices', 'monk citrus variety selection'],
      },
      {
        id: 'cc-19',
        title: 'How Christian Liturgical Calendars Influenced Citrus Harvest Traditions',
        primaryKeyword: 'Christian liturgical calendar citrus harvest traditions',
        secondaryKeywords: ['church calendar citrus harvest timing', 'liturgical season fruit harvest', 'Christian feast day citrus tradition', 'religious calendar citrus growing'],
      },
      {
        id: 'cc-20',
        title: 'How Citrus-Growing Monasteries Became Centers of Education and Medicine',
        primaryKeyword: 'citrus monasteries education medicine centers',
        secondaryKeywords: ['monastery citrus garden education', 'monastic citrus medicine teaching', 'citrus monastery learning center', 'monastery herb citrus garden education'],
      },
      {
        id: 'cc-21',
        title: 'The Medieval Legend of Saint Dominic\'s "Miracle Orange Tree"',
        primaryKeyword: 'Saint Dominic miracle orange tree legend medieval',
        secondaryKeywords: ['Saint Dominic orange tree miracle', 'medieval Christian citrus miracle', 'saints orange tree legend', 'miracle fruit tree Christian legend'],
      },
      {
        id: 'cc-22',
        title: 'How Oranges Became Associated with Christmas Charity Traditions',
        primaryKeyword: 'oranges Christmas charity tradition history',
        secondaryKeywords: ['Christmas orange stocking tradition', 'orange Christmas gift charity', 'St Nicholas orange tradition', 'Christmas citrus charity custom'],
      },
      {
        id: 'cc-23',
        title: 'The Belief That Citrus Trees Could Not Die in a Righteous Man\'s Garden',
        primaryKeyword: 'citrus tree righteous garden folklore belief',
        secondaryKeywords: ['citrus tree spiritual protection belief', 'righteous garden fruit tree legend', 'Christian folklore fruit tree blessing', 'blessed garden citrus belief'],
      },
      {
        id: 'cc-24',
        title: 'How Citrus Was Used in Christian Healing Prayers and House Blessings',
        primaryKeyword: 'citrus Christian healing prayers house blessings',
        secondaryKeywords: ['lemon blessing Christian tradition', 'citrus cleansing prayer house', 'Christian citrus healing ritual', 'house blessing fruit tradition'],
      },
      {
        id: 'cc-25',
        title: 'Citrus Trees as Symbols of Paradise in Christian Graveyards and Memorial Gardens',
        primaryKeyword: 'citrus trees paradise Christian graveyard memorial garden',
        secondaryKeywords: ['cemetery citrus tree symbolism', 'Christian memorial garden fruit tree', 'paradise citrus graveyard tradition', 'memorial garden citrus Christian'],
      },
    ],
  },

  // ───────────────────────────────────────────────
  // GROUP 9: Citrus & World Religions (Judaism, Islam, Hinduism, Buddhism, Folk)
  // ───────────────────────────────────────────────
  {
    id: 'citrus-world-religions',
    name: 'Citrus & World Religions',
    color: '#14B8A6',
    topics: [
      {
        id: 'wr-1',
        title: 'How the Etrog Became One of the Holiest Agricultural Symbols in Judaism',
        primaryKeyword: 'etrog holiest agricultural symbol Judaism',
        secondaryKeywords: ['etrog Sukkot holy significance', 'four species etrog Judaism', 'etrog Jewish agricultural symbol', 'etrog religious importance'],
      },
      {
        id: 'wr-2',
        title: 'Shared Ancestry: How Early Christians and Jews Valued the Same Citrus (Citron)',
        primaryKeyword: 'Christians Jews shared citron citrus ancestry',
        secondaryKeywords: ['citron shared Christian Jewish history', 'citrus Abrahamic religion value', 'citron Jewish Christian significance', 'shared religious citrus traditions'],
      },
      {
        id: 'wr-3',
        title: 'Symbol Comparison: Etrog in Judaism vs. Symbolic Citrus in Christian Art',
        primaryKeyword: 'etrog Judaism vs citrus Christian art symbolism',
        secondaryKeywords: ['Jewish Christian citrus symbol comparison', 'etrog vs orange religious art', 'citrus symbolism Abrahamic religions', 'religious fruit symbol comparison'],
      },
      {
        id: 'wr-4',
        title: 'The Ancient Jewish Trade Networks That Brought Citrus to the Levant',
        primaryKeyword: 'Jewish trade networks citrus Levant ancient',
        secondaryKeywords: ['ancient citrus trade Levant', 'Jewish merchant citrus trade', 'citrus Levant introduction history', 'ancient citrus trade route Israel'],
      },
      {
        id: 'wr-5',
        title: 'How Citrus Influenced Jewish Dietary Law, Ritual Purity, and Festival Practice',
        primaryKeyword: 'citrus Jewish dietary law ritual purity festival',
        secondaryKeywords: ['citrus kosher dietary significance', 'Jewish festival citrus tradition', 'ritual purity citrus Judaism', 'citrus Sukkot Rosh Hashana practice'],
      },
      {
        id: 'wr-6',
        title: 'How Islamic Horticulture Shaped the Citrus Varieties Christians Later Adopted',
        primaryKeyword: 'Islamic horticulture citrus varieties Christians adopted',
        secondaryKeywords: ['Muslim citrus cultivation Christian adoption', 'Islamic agricultural citrus influence', 'Arab citrus varieties Europe', 'Islamic citrus horticulture legacy'],
      },
      {
        id: 'wr-7',
        title: 'The Citrus Gardens of Al-Andalus and Their Influence on Christian Spain',
        primaryKeyword: 'Al-Andalus citrus gardens influence Christian Spain',
        secondaryKeywords: ['Moorish citrus garden Spain', 'Al-Andalus orange garden influence', 'Islamic Spain citrus legacy', 'Andalusian citrus Christian Spain'],
      },
      {
        id: 'wr-8',
        title: 'Why the Qur\'anic Descriptions of Paradise Resemble Citrus Groves',
        primaryKeyword: 'Quran paradise description citrus groves resemblance',
        secondaryKeywords: ['Islamic paradise garden citrus', 'Quran garden fruit descriptions', 'Jannah citrus grove similarity', 'Islamic paradise fruit symbolism'],
      },
      {
        id: 'wr-9',
        title: 'How Muslim Agricultural Scholars Preserved Citrus Knowledge During Europe\'s Dark Ages',
        primaryKeyword: 'Muslim scholars preserved citrus knowledge Dark Ages',
        secondaryKeywords: ['Islamic Golden Age citrus preservation', 'Arab scholars citrus agriculture', 'Muslim citrus knowledge medieval', 'Islamic preservation citrus science'],
      },
      {
        id: 'wr-10',
        title: 'The Citrus Perfumes Used in Islamic Courts and Their Later Adoption by Christian Europe',
        primaryKeyword: 'citrus perfumes Islamic courts Christian Europe adoption',
        secondaryKeywords: ['Islamic citrus perfume history', 'Arab court citrus fragrance', 'citrus perfume Europe Islamic influence', 'neroli orange blossom Islamic courts'],
      },
      {
        id: 'wr-11',
        title: 'The Sacred Indian Origins of Many Citrus Species Used by Later Christian Cultures',
        primaryKeyword: 'sacred Indian origins citrus species Christian cultures',
        secondaryKeywords: ['India citrus origin sacred', 'citrus species Indian heritage', 'Hindu citrus Western adoption', 'Indian citrus global spread religious'],
      },
      {
        id: 'wr-12',
        title: 'Citrus in Ayurveda: How Ancient Hindu Medicine Influenced Christian Healing Practices',
        primaryKeyword: 'citrus Ayurveda Hindu medicine Christian healing influence',
        secondaryKeywords: ['Ayurvedic citrus medicine history', 'Hindu healing Christian adoption', 'Ayurveda lemon lime medicine', 'citrus Ayurveda Western medicine'],
      },
      {
        id: 'wr-13',
        title: 'Lemons in Hindu Ritual Purification vs. Lemons in Medieval Christian Medicine',
        primaryKeyword: 'lemon Hindu ritual purification vs Christian medicine',
        secondaryKeywords: ['lemon purification Hindu Christian comparison', 'lemon spiritual cleansing traditions', 'Hindu lemon ritual vs medieval lemon medicine', 'cross-cultural lemon use'],
      },
      {
        id: 'wr-14',
        title: 'Indian Citrus Varieties That Traveled Through Trade Routes into Christian Lands',
        primaryKeyword: 'Indian citrus varieties trade routes Christian lands',
        secondaryKeywords: ['Indian citrus export trade history', 'citrus varieties India to Europe', 'Indian citrus trade route history', 'citrus spice trade India'],
      },
      {
        id: 'wr-15',
        title: 'The Symbolic Meaning of Citrus in Hindu Festivals and How It Parallels Christian Symbolism',
        primaryKeyword: 'citrus Hindu festivals parallels Christian symbolism',
        secondaryKeywords: ['citrus festival symbolism comparison', 'Hindu Christian fruit ceremony parallels', 'Diwali citrus Christian Christmas orange', 'cross-cultural citrus symbolism'],
      },
      {
        id: 'wr-16',
        title: 'Why Yuzu Baths Became a Buddhist Midwinter Healing Ritual in Japan',
        primaryKeyword: 'yuzu bath Buddhist midwinter healing ritual Japan',
        secondaryKeywords: ['toji yuzu bath Buddhist tradition', 'Japanese Buddhist yuzu healing', 'winter solstice yuzu bath Buddhism', 'yuzu therapeutic Buddhist ritual'],
      },
      {
        id: 'wr-17',
        title: 'The Aesthetic Role of Citrus in Zen Temple Gardens',
        primaryKeyword: 'citrus Zen temple gardens aesthetic role',
        secondaryKeywords: ['Zen garden citrus tree design', 'Japanese temple citrus aesthetic', 'Zen Buddhism citrus garden', 'citrus mindfulness garden Zen'],
      },
      {
        id: 'wr-18',
        title: 'Buddhist Teachings Using Citrus Metaphors (Inner Sweetness vs. Outer Bitterness)',
        primaryKeyword: 'Buddhist citrus metaphor inner sweetness outer bitterness',
        secondaryKeywords: ['Buddhism citrus teaching analogy', 'citrus Buddhist philosophy metaphor', 'inner outer nature citrus Buddhism', 'Buddhist fruit wisdom teaching'],
      },
      {
        id: 'wr-19',
        title: 'How Japanese Buddhist Monks Helped Spread Early Citrus Varieties',
        primaryKeyword: 'Japanese Buddhist monks spread citrus varieties',
        secondaryKeywords: ['Buddhist monk citrus cultivation Japan', 'Japanese temple citrus spreading', 'monk citrus variety distribution', 'Buddhist monastery citrus Japan'],
      },
      {
        id: 'wr-20',
        title: 'Citrus Symbolism in Buddhist Concepts of Impermanence and Fragrance',
        primaryKeyword: 'citrus symbolism Buddhist impermanence fragrance',
        secondaryKeywords: ['Buddhism impermanence citrus analogy', 'citrus fragrance Buddhist philosophy', 'Buddhist citrus transience symbol', 'citrus blossom impermanence teaching'],
      },
      {
        id: 'wr-21',
        title: 'Australian Aboriginal Spiritual Uses of Finger Limes Long Before Global Discovery',
        primaryKeyword: 'Aboriginal spiritual uses finger limes Australia',
        secondaryKeywords: ['Aboriginal finger lime spiritual tradition', 'indigenous Australian citrus ceremony', 'finger lime Aboriginal bush medicine', 'first nations citrus spiritual'],
      },
      {
        id: 'wr-22',
        title: 'Southeast Asian Animistic Traditions Using Makrut Lime for Protection Rituals',
        primaryKeyword: 'Southeast Asian makrut lime protection rituals animism',
        secondaryKeywords: ['makrut lime spiritual protection', 'Thai kaffir lime ceremony', 'Southeast Asian lime ritual tradition', 'animist citrus protection ritual'],
      },
      {
        id: 'wr-23',
        title: 'How Citrus Trees Became Symbols of Good Fortune in Chinese Folk Religion',
        primaryKeyword: 'citrus good fortune Chinese folk religion symbol',
        secondaryKeywords: ['Chinese New Year citrus good luck', 'mandarin orange fortune Chinese', 'citrus Chinese folk belief prosperity', 'kumquat good luck Chinese tradition'],
      },
      {
        id: 'wr-24',
        title: 'The Use of Citrus in Protective Magic and Warding Rituals Across Cultures',
        primaryKeyword: 'citrus protective magic warding rituals cultures',
        secondaryKeywords: ['lemon warding evil magic', 'citrus protection spell traditions', 'folk magic citrus ritual', 'citrus evil eye protection cultures'],
      },
      {
        id: 'wr-25',
        title: 'Citrus as a Boundary Marker in Ancient Pagan and Pre-Christian European Rites',
        primaryKeyword: 'citrus boundary marker pagan pre-Christian European rites',
        secondaryKeywords: ['pagan citrus ritual European', 'pre-Christian citrus boundary tradition', 'ancient European fruit ritual', 'citrus pagan ceremony Europe'],
      },
    ],
  },
];

export function getTotalTopicCount(): number {
  return TOPICAL_CONTENT_GROUPS.reduce((sum, group) => sum + group.topics.length, 0);
}

export function findTopicById(topicId: string): TopicItem | undefined {
  for (const group of TOPICAL_CONTENT_GROUPS) {
    const topic = group.topics.find(t => t.id === topicId);
    if (topic) return topic;
  }
  return undefined;
}

export function findGroupByTopicId(topicId: string): TopicGroup | undefined {
  return TOPICAL_CONTENT_GROUPS.find(group =>
    group.topics.some(t => t.id === topicId)
  );
}
